/**
 * The four writes the JMC user screens make, each as a dialog.
 *
 * They live together because they share one thing that is easy to get wrong
 * separately: what a JMC operator IS. A platform operator holds no clinic
 * membership and a clinic person holds no platform role - migration 011 enforces
 * it with a trigger in both directions - so every one of these dialogs has to
 * say so before the server refuses. A form that lets somebody fill it in and
 * then reports a constraint violation has wasted their time to teach them a rule
 * it could have stated.
 *
 * Membership shapes follow the data model rather than the screen: a grant is the
 * join row between a user and a clinic, so ADDING one names a clinic (there is no
 * grant without one) and CHANGING one addresses the row by its own id and needs
 * no clinic at all.
 */
import { ROLE_REACH, roleOptions, type TenantRoleName } from '../roles.ts';
import { useState } from 'react';
import type { AdminClinic, AdminStore } from '../api.ts';
import { post } from '../api.ts';
import { ModalForm } from './Modal.tsx';
import { Field, FieldGroup } from './EditPanel.tsx';
import { Select } from './Select.tsx';
import { PASSWORD_HINT, MIN_PASSWORD_LENGTH } from '../../../shared/password-policy.ts';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * A clinic and store picker, shared by "add a membership" and "create a user".
 *
 * One component because the cascade has a rule in it: choosing a clinic has to
 * reset the store, or the second dialog submits a store belonging to the clinic
 * that was selected first - which the server refuses, correctly, with a message
 * about another tenant that makes no sense to whoever is looking at the form.
 */
function ScopeFields({
  clinics,
  stores,
  clinicId,
  setClinicId,
  role,
  setRole,
  storeId,
  setStoreId,
  idPrefix,
}: {
  clinics: AdminClinic[];
  stores: AdminStore[];
  clinicId: string;
  setClinicId: (value: string) => void;
  role: string;
  setRole: (value: string) => void;
  storeId: string;
  setStoreId: (value: string) => void;
  idPrefix: string;
}) {
  const clinicStores = stores.filter((store) => store.clinic_id === clinicId);

  return (
    <>
      <Field label="クリニック" htmlFor={`${idPrefix}-clinic`} required>
        <Select
          id={`${idPrefix}-clinic`}
          value={clinicId}
          onChange={(value) => {
            setClinicId(value);
            setStoreId('');
          }}
          options={[
            { value: '', label: '選択してください' },
            ...clinics.map((clinic) => ({ value: clinic.id, label: clinic.name })),
          ]}
        />
      </Field>

      <Field
        label="権限"
        htmlFor={`${idPrefix}-role`}
        hint={ROLE_REACH[role as TenantRoleName] ?? ''}
      >
        <Select
          id={`${idPrefix}-role`}
          value={role}
          onChange={setRole}
          options={roleOptions(true)}
        />
      </Field>

      {role !== 'CLINIC_ADMIN' && (
        <Field
          label="担当店舗"
          htmlFor={`${idPrefix}-store`}
          required
          {...(clinicStores.length === 0
            ? { hint: 'このクリニックには店舗がありません。' }
            : {})}
        >
          <Select
            id={`${idPrefix}-store`}
            value={storeId}
            onChange={setStoreId}
            options={[
              { value: '', label: '選択してください' },
              ...clinicStores.map((store) => ({ value: store.id, label: store.name })),
            ]}
          />
        </Field>
      )}
    </>
  );
}

/** Enough of a user for every dialog here; both list and detail rows satisfy it. */
export function CreateUserDialog({
  clinics,
  stores,
  open,
  onClose,
}: DialogProps & { clinics: AdminClinic[]; stores: AdminStore[] }) {
  const [kind, setKind] = useState<'clinic' | 'platform'>('clinic');
  const [form, setForm] = useState({ displayName: '', email: '', password: '' });
  const [clinicId, setClinicId] = useState('');
  const [role, setRole] = useState('CLINIC_ADMIN');
  const [storeId, setStoreId] = useState('');

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title="ユーザーを追加"
      caption="招待メールは送られません。このアドレスでGoogleログインした人が、このアカウントを引き継ぎます。"
      submitLabel="追加する"
      onSubmit={() =>
        post('/admin/users', {
          displayName: form.displayName,
          email: form.email,
          // Omitted when blank rather than sent empty: the schema's minimum
          // would refuse '' and the normal answer IS blank.
          ...(form.password ? { password: form.password } : {}),
          ...(kind === 'platform'
            ? { platformRole: 'JMC_ADMIN' }
            : { clinicId, role, ...(role !== 'CLINIC_ADMIN' ? { storeId } : {}) }),
        })
      }
    >
      <FieldGroup title="種別" note="所属とJMC管理者権限は同時に持てません。" />
      <div className="chips field--full" style={{ marginBottom: 10 }}>
        <button
          type="button"
          className="chip"
          aria-pressed={kind === 'clinic'}
          onClick={() => setKind('clinic')}
        >
          クリニックに追加
        </button>
        <button
          type="button"
          className="chip"
          aria-pressed={kind === 'platform'}
          onClick={() => setKind('platform')}
        >
          JMC管理者として作成
        </button>
      </div>

      <Field label="表示名" htmlFor="cu-name" required>
        <input
          id="cu-name"
          value={form.displayName}
          maxLength={200}
          required
          onChange={(event) => setForm({ ...form, displayName: event.target.value })}
        />
      </Field>
      <Field label="メールアドレス" htmlFor="cu-email" required>
        <input
          id="cu-email"
          type="email"
          value={form.email}
          maxLength={320}
          required
          onChange={(event) => setForm({ ...form, email: event.target.value })}
        />
      </Field>

      {/*
        Optional, and the normal answer is blank: most accounts sign in with
        Google and hold no password at all. It is here because an account with
        neither Google nor a password cannot sign in, and setting one at creation
        beats sending somebody to a second screen they will not visit.
      */}
      <Field
        label="パスワード（任意）"
        htmlFor="cu-password"
        span="full"
        hint={`空欄ならGoogleログインのみになります。${PASSWORD_HINT}。あとから本人がプロフィール画面で設定・変更できます。`}
      >
        <input
          id="cu-password"
          type="password"
          autoComplete="new-password"
          value={form.password}
          minLength={MIN_PASSWORD_LENGTH}
          maxLength={200}
          placeholder={PASSWORD_HINT}
          onChange={(event) => setForm({ ...form, password: event.target.value })}
        />
      </Field>

      {kind === 'clinic' ? (
        <ScopeFields
          clinics={clinics}
          stores={stores}
          clinicId={clinicId}
          setClinicId={setClinicId}
          role={role}
          setRole={setRole}
          storeId={storeId}
          setStoreId={setStoreId}
          idPrefix="cu"
        />
      ) : (
        <p className="alert field--full" role="status">
          JMC管理者は全クリニックの情報を扱えます。作成時に再認証が必要です。記事本文の閲覧には、
          別途サポート権限昇格が必要です。
        </p>
      )}
    </ModalForm>
  );
}


